/**
 * Writing out a day the billing API sent.
 *
 * The endpoints send two shapes and they must not be read the same way:
 *
 *   - **A calendar day** — `"2026-07-02"`, which is what `payment_date` and the
 *     cycle dates are. `new Date("2026-07-02")` reads that as midnight *UTC*,
 *     which is the previous day for anyone west of Greenwich — so a payment
 *     taken on the 2nd prints as the 1st, and a window closing on the 9th
 *     prints as the 8th. Pinning it to local midnight is what stops that.
 *   - **A moment** — `"2026-07-09T10:09:51+00:00"`, which is what
 *     `window_ends_at` and the refund timestamps are. That one *is* an instant,
 *     and the day it falls on is the reader's own, so it is parsed as it stands
 *     and rendered in their zone. Slicing the date out of the string would
 *     print the backend's day rather than theirs.
 *
 * Both used to be handled by a `localDay` copied into two cards, which only
 * knew about the first — the refund window is a timestamp now, and a helper
 * that slices ten characters off one is a helper that quietly shows the wrong
 * date to half the world.
 */

import { formatCreatedOn } from "../brand/planFormat";

/** Whether the string carries a time, and so is a moment rather than a day. */
const isMoment = (iso: string): boolean => iso.includes("T");

/**
 * An ISO day or timestamp as a `Date` in the reader's own zone.
 *
 * `null` for anything unusable — an empty string, or a value the browser could
 * not parse — so a caller can fall back to saying nothing rather than printing
 * "Invalid Date" onto a card about somebody's money.
 */
export const billingDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;

  const date = isMoment(iso)
    ? new Date(iso)
    : // A bare day, pinned to local midnight — see the note above.
      new Date(`${iso.slice(0, 10)}T00:00:00`);

  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * A day the backend sent, in the format the rest of the app writes dates in:
 * `9 Jul 2026`. `null` where there was nothing to write.
 */
export const formatBillingDate = (
  iso: string | null | undefined
): string | null => {
  const date = billingDate(iso);
  return date ? formatCreatedOn(date) : null;
};

/**
 * Whether a moment has already passed.
 *
 * Used only to tell one closed offer from another when writing copy — a window
 * that ran out is a different sentence from one that never applied. It decides
 * nothing about money: `eligible` off the refund endpoint is what says whether
 * a refund can be asked for, and that answer is the server's alone.
 */
export const hasPassed = (
  iso: string | null | undefined,
  now: Date = new Date()
): boolean => {
  const date = billingDate(iso);
  return !!date && date.getTime() <= now.getTime();
};
